import React from "react";
import { BootstrapTable, TableHeaderColumn } from "react-bootstrap-table";
import Button from "../../components/subcomponents/controls/button";
import InputField from "../../components/subcomponents/controls/inputfield";
import SelectField from "../../components/subcomponents/controls/selectfield";
import { connect } from "react-redux";
import calendar from "../../api/calendar";
import notification from "../../components/subcomponents/notification";
import SubformDetails from "../../components/subcomponents/subform_details";
import FormHeader from "../../components/subcomponents/formheader";
import moment from "moment-timezone";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";

countries.registerLocale(enLocale);

// --- Dropdown option builders (derived from libraries, always up-to-date) ---

const TIMEZONE_OPTIONS = moment.tz.names().map((tz) => {
    const offset = moment.tz(tz).format("Z");
    return { value: tz, label: "(UTC" + offset + ") " + tz.replace(/_/g, " ") };
});

const COUNTRY_OPTIONS = Object.entries(countries.getNames("en"))
    .map(([code, name]) => ({ value: code, label: name }))
    .sort((a, b) => a.label.localeCompare(b.label));

const TYPE_OPTIONS = [
    { value: "motocross", label: "Motocross" },
    { value: "karting", label: "Karting" },
    { value: "car_racing", label: "Car Racing" },
    { value: "motorcycle_racing", label: "Motorcycle Racing" },
    { value: "other", label: "Other" },
];

const EMPTY_FORM = {
    start_time: "",
    end_time: "",
    timezone: "Europe/Riga",
    championship: "",
    country: "LV",
    track_name: "",
    type: "",
    banner_url: "",
};

// --- Helpers ---

function unixToDateString(unix) {
    if (!unix) return "";
    let d = new Date(unix * 1000);
    let pad = (n) => String(n).padStart(2, "0");
    return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
}

function dateStringToUnix(val) {
    if (!val) return "";
    return Math.floor(new Date(val + "T00:00:00").getTime() / 1000);
}

function formatTimestamp(unix) {
    if (!unix) return "";
    return new Date(unix * 1000).toLocaleString();
}

function typeLabelLookup(val) {
    let found = TYPE_OPTIONS.find((o) => o.value === val);
    return found ? found.label : val || "";
}

function countryLabelLookup(code) {
    if (!code) return "";
    return countries.getName(code, "en") || code;
}

// --- Component ---

class CalendarList extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            allow_to_edit: false,
            edit_rec: false,
            new_rec: false,
            form: { ...EMPTY_FORM },
        };
    }

    componentWillMount() {
        calendar.list();
        if (this.props.role === "Admin") {
            calendar.users_list();
        }
        this.props.handleSelect();
    }

    parseFields(fields) {
        if (!fields) return null;
        if (typeof fields === "object") return fields;
        try { return JSON.parse(fields); } catch (e) { return null; }
    }

    getTableData() {
        let list = this.props.calendar_list;
        if (!list || !list.length) return [];
        return list.map((entry) => {
            let p = this.parseFields(entry.fields) || {};
            return {
                id: entry.id,
                user_name: entry.user_name || "",
                start_time_display: formatTimestamp(p.start_time),
                end_time_display: formatTimestamp(p.end_time),
                timezone: p.timezone || "",
                championship: p.championship || "",
                country_display: countryLabelLookup(p.country),
                track_name: p.track_name || "",
                type_display: typeLabelLookup(p.type),
            };
        });
    }

    selectItem(id) {
        if (this.state.new_rec || this.state.allow_to_edit) {
            notification.error("New/edit rec mode, please cancel before select");
            return null;
        }
        if (id) {
            this.setState({ edit_rec: true, allow_to_edit: false });
            this.props.handleSelect(id);
            calendar.details(() => {
                let parsed = this.parseFields(this.props.calendar_details.fields);
                if (parsed) {
                    this.setState({
                        form: {
                            start_time: unixToDateString(parsed.start_time),
                            end_time: unixToDateString(parsed.end_time),
                            timezone: parsed.timezone || "",
                            championship: parsed.championship || "",
                            country: parsed.country || "",
                            track_name: parsed.track_name || "",
                            type: parsed.type || "",
                            banner_url: parsed.banner_url || "",
                        },
                    });
                } else {
                    this.setState({ form: { ...EMPTY_FORM } });
                }
            });
        } else {
            this.cancel();
        }
    }

    cancel() {
        this.setState({
            new_rec: false, edit_rec: false, allow_to_edit: false,
            form: { ...EMPTY_FORM },
        });
        this.props.handleSelect();
        calendar.list();
    }

    newRec() {
        this.props.handleSelect();
        this.setState({ new_rec: true, form: { ...EMPTY_FORM } });
    }

    formChange(field, value) {
        this.setState({ form: { ...this.state.form, [field]: value } });
    }

    isFormValid() {
        let f = this.state.form;
        return (
            f.start_time && f.end_time && f.timezone &&
            f.championship && f.country && f.track_name && f.type
        );
    }

    saveRec() {
        let self = this;
        if (!this.isFormValid()) {
            notification.error("All fields are mandatory");
            return;
        }
        let fieldsObj = {
            start_time: dateStringToUnix(this.state.form.start_time),
            end_time: dateStringToUnix(this.state.form.end_time),
            timezone: this.state.form.timezone,
            championship: this.state.form.championship,
            country: this.state.form.country,
            track_name: this.state.form.track_name,
            type: this.state.form.type,
            banner_url: (this.state.form.banner_url || "").trim(),
        };
        if (fieldsObj.end_time < fieldsObj.start_time) {
            notification.error("End date must not be before start date");
            return;
        }
        this.props.fieldChange({ name: "fields", value: JSON.stringify(fieldsObj) });
        setTimeout(() => {
            if (self.state.edit_rec) {
                calendar.update((error) => {
                    if (!error) { calendar.list(); self.cancel(); }
                });
            } else {
                calendar.add((error) => {
                    if (!error) { calendar.list(); self.cancel(); }
                });
            }
        }, 50);
    }

    deleteRec() {
        let self = this;
        calendar.del((error) => {
            if (!error) { calendar.list(); self.cancel(); }
        });
    }

    showImagePreview(url) {
        if (!url) return null;
        return (
            <img
                className="branding_preview"
                src={url}
                alt=""
                onError={(e) => (e.target.style.display = "none")}
                onLoad={(e) => (e.target.style.display = "")}
            />
        );
    }

    // --- Admin user filter ---

    onUserFilterChange(result) {
        this.props.setUserFilter(result.value);
        if (result.value) {
            this.props.setFilter({ user_id: result.value });
        } else {
            this.props.setFilter({});
        }
        setTimeout(() => calendar.list(), 50);
    }

    showUserFilter() {
        if (this.props.role !== "Admin") return null;
        let options = (this.props.users_list || []).map((u) => ({
            value: u.value, label: u.label,
        }));
        return (
            <div className="calendar_user_filter" style={{ marginBottom: 10 }}>
                <SelectField
                    status="enabled" name="user_filter" label="Filter by user"
                    options={options} allowEmpty={true}
                    onChange={this.onUserFilterChange.bind(this)}
                    value={this.props.filter_user_id || ""}
                />
            </div>
        );
    }

    // --- Table ---

    actionsFormatter(cell, row) {
        return (
            <div>
                <button className="button edit" onClick={(e) => { e.stopPropagation(); this.selectItem(row.id); }}>Edit</button>
                <button className="button delete" style={{ marginLeft: 5 }} onClick={(e) => {
                    e.stopPropagation();
                    this.props.handleSelect(row.id);
                    setTimeout(() => {
                        calendar.del((error) => { if (!error) { calendar.list(); this.cancel(); } });
                    }, 50);
                }}>Delete</button>
            </div>
        );
    }

    showTable() {
        let data = this.getTableData();
        let selectRow = {
            mode: "radio", clickToSelect: true,
            onSelect: (row, isSelected) => { if (isSelected) this.selectItem(row.id); else this.selectItem(); },
            bgColor: "#E4F2F3", hideSelectColumn: true,
            selected: [this.props.calendar_selected],
        };
        return (
            <div className="table_wrap calendar_table">
                <BootstrapTable ref="table" selectRow={selectRow} hover={true} keyField="id"
                    bodyStyle={{ wordBreak: "break-all" }} data={data} striped
                    options={{ noDataText: "No calendar entries" }}>
                    <TableHeaderColumn dataField="id" width="50">ID</TableHeaderColumn>
                    {this.props.role === "Admin" && (
                        <TableHeaderColumn dataField="user_name" width="100">User</TableHeaderColumn>
                    )}
                    <TableHeaderColumn dataField="championship" width="130">Championship</TableHeaderColumn>
                    <TableHeaderColumn dataField="track_name" width="120">Track</TableHeaderColumn>
                    <TableHeaderColumn dataField="type_display" width="100">Type</TableHeaderColumn>
                    <TableHeaderColumn dataField="country_display" width="100">Country</TableHeaderColumn>
                    <TableHeaderColumn dataField="start_time_display" width="140">Start</TableHeaderColumn>
                    <TableHeaderColumn dataField="end_time_display" width="140">End</TableHeaderColumn>
                    <TableHeaderColumn dataField="timezone" width="110">Timezone</TableHeaderColumn>
                    <TableHeaderColumn dataField="actions" width="140" dataFormat={this.actionsFormatter.bind(this)}>Actions</TableHeaderColumn>
                </BootstrapTable>
            </div>
        );
    }

    // --- Details form ---

    showDetails() {
        let details_title = this.state.edit_rec ? "Edit calendar entry" : "New calendar entry";
        let save_btn_label = this.state.edit_rec ? "Update" : "Add new";
        let save_btn = this.isFormValid() ? "enabled" : "disabled";
        if (this.state.edit_rec && !this.state.allow_to_edit) save_btn = "hidden";
        let del_btn = this.state.edit_rec && this.state.allow_to_edit ? "enabled" : "hidden";
        let inputs_enabled = this.state.allow_to_edit || this.state.new_rec ? "enabled" : "disabled";

        if (!this.state.new_rec && !this.state.edit_rec) return null;

        let f = this.state.form;
        return (
            <SubformDetails is_hidden={false} title={details_title} className="details width_50">
                {this.state.edit_rec && (
                    <div className="field_wrap">
                        <label>
                            <input type="checkbox" checked={this.state.allow_to_edit}
                                onChange={(e) => this.setState({ allow_to_edit: e.target.checked })} />
                            {" "}Enable edit
                        </label>
                    </div>
                )}
                <InputField status={inputs_enabled} name="championship" label="Championship"
                    onChange={(r) => this.formChange("championship", r.value)} value={f.championship} />
                <InputField status={inputs_enabled} name="track_name" label="Track Name"
                    onChange={(r) => this.formChange("track_name", r.value)} value={f.track_name} />
                <SelectField status={inputs_enabled} name="type" label="Type"
                    options={TYPE_OPTIONS} onChange={(r) => this.formChange("type", r.value)} value={f.type} />
                <SelectField status={inputs_enabled} name="country" label="Country"
                    options={COUNTRY_OPTIONS} onChange={(r) => this.formChange("country", r.value)} value={f.country} />
                <SelectField status={inputs_enabled} name="timezone" label="Timezone"
                    options={TIMEZONE_OPTIONS} onChange={(r) => this.formChange("timezone", r.value)} value={f.timezone} />
                <div className="field_wrap">
                    <label>Start Date</label>
                    <input type="date" value={f.start_time}
                        disabled={inputs_enabled === "disabled"}
                        onChange={(e) => this.formChange("start_time", e.target.value)}
                        style={{ width: "100%" }} />
                </div>
                <div className="field_wrap">
                    <label>End Date</label>
                    <input type="date" value={f.end_time}
                        disabled={inputs_enabled === "disabled"}
                        onChange={(e) => this.formChange("end_time", e.target.value)}
                        style={{ width: "100%" }} />
                </div>
                <InputField status={inputs_enabled} name="banner_url" label="Banner URL"
                    placeholder="https://..." maxlength={500}
                    onChange={(r) => this.formChange("banner_url", r.value)} value={f.banner_url || ""} />
                {this.showImagePreview(f.banner_url)}
                <div className="toolbar" style={{ marginTop: 12 }}>
                    <Button name="save" label={save_btn_label} onClick={this.saveRec.bind(this)} status={save_btn} />
                    <Button name="delete" label="Delete entry" onClick={this.deleteRec.bind(this)} status={del_btn} />
                    <Button name="cancel" label="Cancel" onClick={this.cancel.bind(this)} />
                </div>
            </SubformDetails>
        );
    }

    render() {
        let new_btn = this.state.edit_rec ? "hidden" : "enabled";
        return (
            <div className="form_wrap calendar_list">
                <FormHeader title="RACE CALENDAR" />
                <SubformDetails className="master width_50">
                    {this.showUserFilter()}
                    {this.showTable()}
                    <Button name="new" label="New entry" onClick={this.newRec.bind(this)} status={new_btn} />
                </SubformDetails>
                {this.showDetails()}
            </div>
        );
    }
}

function mapStateToProps(state) {
    let cal = state.calendarR.calendar;
    return {
        search_string: cal.search_string,
        calendar_list: cal.list,
        calendar_selected: cal.selected,
        calendar_details: cal.details,
        calendar_validation: cal.validation,
        filter_user_id: cal.filter_user_id,
        users_list: cal.users_list,
        role: state.authR.user.role,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        handleSearch(result) {
            dispatch({ type: "CALENDAR_DESELECTED" });
            dispatch({ type: "CALENDAR_UPDATE_SEARCH_STRING", value: result.value });
            calendar.list();
        },
        handleSelect(id) {
            if (id) { dispatch({ type: "CALENDAR_SELECTED", value: id }); }
            else { dispatch({ type: "CALENDAR_DESELECTED" }); }
        },
        fieldChange(result) {
            dispatch({ type: "CALENDAR_UPDATE_FORM", field: result.name, value: result.value });
        },
        setUserFilter(value) {
            dispatch({ type: "CALENDAR_SET_USER_FILTER", value: value });
        },
        setFilter(filter) {
            dispatch({ type: "CALENDAR_SET_FILTER", filter: filter });
        },
    };
}

export default CalendarList = connect(mapStateToProps, mapDispatchToProps)(CalendarList);
